/**
 * Shared helpers for mapping an Adyen decline/refusal reason (or a terminal device's raw
 * acquirer response text) into the DMS-facing `errorMessage` field.
 *
 * Mirrors the backend's equivalent helpers in
 * src/TargetPay.WebHooks/Services/impl/AdyenWebHookProcessorService.cs
 * (SanitizeDeclineErrorMessage / ResolveDeclineErrorMessage) so both layers apply the same
 * sanitization and payment-type scoping rules.
 */

/**
 * Payment types for which a declined/failed reason is mapped into the DMS-facing errorMessage
 * field. Applies to all payment types PerseusPay supports (Manual, Terminal, SavedCard,
 * PayByLink, ACH) — no exclusions.
 */
export const DECLINE_REASON_ELIGIBLE_PAYMENT_TYPES = ["Manual", "Terminal", "SavedCard", "PayByLink", "ACH"];

/**
 * Sanitizes a decline reason before it is written into the DMS-facing errorMessage field.
 * The value is untrusted external input (relayed by Adyen from the acquirer/issuer, or read
 * directly off a terminal device response), so it is reduced to Latin letters and single spaces
 * only. This removes control characters and punctuation that could otherwise enable injection
 * into a downstream payload, without affecting Adyen's standard human-readable refusal reasons
 * (e.g. "Not enough balance"). Deliberately has no length cap — these are short, fixed phrases.
 */
export function sanitizeDeclineErrorMessage(reason: string | null | undefined): string {
  if (!reason || !reason.trim()) {
    return "";
  }

  const COMBINING_DIACRITICAL_MARKS = new RegExp("[\\u0300-\\u036f]", "g");
  const noAccents = reason.normalize("NFD").replace(COMBINING_DIACRITICAL_MARKS, "");
  const lettersAndSpacesOnly = noAccents.replace(/[^a-zA-Z ]/g, " ");

  return lettersAndSpacesOnly.replace(/\s+/g, " ").trim();
}

/**
 * Resolves the DMS-facing errorMessage for a declined/failed payment, formatted as
 * "Reason: {sanitized reason}". Only applies to the payment types in
 * DECLINE_REASON_ELIGIBLE_PAYMENT_TYPES; returns an empty string otherwise (including when
 * reason is empty/absent), consistent with the existing contract.
 */
export function resolveDeclineErrorMessage(
  reason: string | null | undefined,
  paymentType: string | null | undefined,
): string {
  if (!paymentType) {
    return "";
  }

  const isEligible = DECLINE_REASON_ELIGIBLE_PAYMENT_TYPES.some(
    (eligibleType) => eligibleType.toLowerCase() === paymentType.toLowerCase(),
  );

  if (!isEligible) {
    return "";
  }

  const sanitized = sanitizeDeclineErrorMessage(reason);

  return sanitized ? `Reason: ${sanitized}` : "";
}
